import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import { BreadcrumbModel } from '../../models/breadcrumb.model';

/**
 * Section labels for pages that never call updateBreadcrumbs. Longest prefix wins, so
 * 'cashering/admin-drawers' beats 'cashering'.
 */
const SECTION_CRUMBS: Record<string, BreadcrumbModel> = {
  'cashering/admin-drawers': { label: 'Cash Drawers', icon: 'account_balance_wallet' },
  'cashering': { label: 'Take a Payment', icon: 'point_of_sale' },
  'tasks': { label: 'Tasks', icon: 'task' },
  'search': { label: 'Search', icon: 'search' },
  'discovery': { label: 'Discovery', icon: 'travel_explore' },
  'distributions': { label: 'Distributions', icon: 'pie_chart' },
  'reports': { label: 'Reports', icon: 'description' },
  'dashboard': { label: 'Dashboards', icon: 'dashboard' },
  'correspondence': { label: 'Correspondence', icon: 'mail' },
  'admin': { label: 'Administrative', icon: 'work' },
  'query': { label: 'Query', icon: 'manage_search' },
};

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private breadcrumbs: BreadcrumbModel[] = [];

  // Observable to track breadcrumb changes
  breadcrumbs$ = new BehaviorSubject<BreadcrumbModel[]>(this.breadcrumbs);

  constructor(router: Router) {
    // Pages set their own crumbs imperatively, and many never do, so the previous page's
    // crumb used to linger (e.g. "Search" on a discovery case). Reset to the section on every
    // navigation; a page that sets its own crumbs overrides this once it initialises.
    router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(e => this.updateBreadcrumbs(this.sectionCrumbs(e.url)));
  }

  // Method to update breadcrumbs
  updateBreadcrumbs(newBreadcrumbs: BreadcrumbModel[]): void {
    this.breadcrumbs = newBreadcrumbs;
    this.breadcrumbs$.next(this.breadcrumbs);
  }

  private sectionCrumbs(url: string): BreadcrumbModel[] {
    const path = url.split(/[?#]/)[0].replace(/^\/app\/?/, '');
    const key = Object.keys(SECTION_CRUMBS)
      .filter(k => path === k || path.startsWith(k + '/'))
      .sort((a, b) => b.length - a.length)[0];
    return key ? [{ ...SECTION_CRUMBS[key], url: '/app/' + key }] : [];
  }
}
